import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import {
  Alert,
  Box,
  Button,
  Grid,
  Heading,
  Image,
  Text,
} from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import {
  normalizeGalleryBlockData,
  type GalleryBlockData,
} from '#pages/document/editorjs-types';

import {
  NP_GALLERY_TAG,
  parseMarkdownGalleryConfig,
  serializeMarkdownGalleryBlock,
  serializeMarkdownGalleryInnerConfig,
  stringifyMarkdownGalleryConfig,
} from './markdown-gallery';

interface MarkdownGalleryNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownGalleryNodeAttrs {
  data: GalleryBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeGalleryAttrs(attrs: unknown): MarkdownGalleryNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeGalleryBlockData(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownGalleryNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownGalleryNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeGalleryAttrs(node.attrs);
  const itemCount = attrs.data.items.length;
  const columnCount = attrs.data.columnCount ?? 3;

  const handleEdit = () => {
    context.onRequestGalleryEdit?.({
      initialJson: stringifyMarkdownGalleryConfig(attrs.data),
      onCommit: async (next) => {
        const normalized = normalizeGalleryBlockData(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyMarkdownGalleryConfig(normalized),
          parseError: null,
        });
      },
    });
  };

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-gallery-node markdown-gallery-node--selected'
          : 'markdown-gallery-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
      >
        <Box>
          <Heading as="h2" size="sm">
            Gallery
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            {itemCount === 0
              ? `Empty gallery, ${columnCount} columns`
              : `${itemCount} image${itemCount === 1 ? '' : 's'}, ${columnCount} columns`}
          </Text>
        </Box>
        <Button
          size="xs"
          variant="outline"
          onClick={handleEdit}
          disabled={!context.onRequestGalleryEdit}
        >
          Edit gallery
        </Button>
      </Box>

      {attrs.parseError ? (
        <Alert.Root status="warning" mt={3} borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid gallery config</Alert.Title>
            <Alert.Description>{attrs.parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {itemCount > 0 ? (
        <Grid
          mt={3}
          templateColumns={`repeat(${columnCount}, minmax(0, 1fr))`}
          gap={3}
        >
          {attrs.data.items.map((item) => (
            <Box key={`${item.url}-${item.caption ?? ''}`}>
              <Image
                src={item.url}
                alt={item.caption ?? ''}
                borderRadius="md"
                objectFit="cover"
                aspectRatio={1}
                width="100%"
              />
              {item.caption ? (
                <Text mt={1} fontSize="sm" color="fg.muted">
                  {item.caption}
                </Text>
              ) : null}
            </Box>
          ))}
        </Grid>
      ) : (
        <Box
          mt={3}
          px={4}
          py={5}
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="md"
          color="fg.muted"
        >
          Use Edit gallery to add images.
        </Box>
      )}
    </NodeViewWrapper>
  );
}

export const MarkdownGalleryNode = Node.create<MarkdownGalleryNodeOptions>({
  name: 'npGallery',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeGalleryBlockData({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_GALLERY_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownGalleryConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeGalleryAttrs(HTMLAttributes);
    return [NP_GALLERY_TAG, serializeMarkdownGalleryInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeGalleryAttrs(node?.attrs);
    return serializeMarkdownGalleryBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownGalleryNodeView);
  },
});
